import React from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Check, Crown, Sparkles } from "lucide-react";
import { startCheckout, openPortal } from "../components/Paywall";

const FEATURES = [
  "Unlimited bills & income sources",
  "Newspaper-style PDF ledger export",
  "6-month cashflow forecast chart",
  "Historical months — any month, any year",
  "Priority overdue email reminders",
  "Ad-free experience",
];

export default function PricingPage() {
  const { user } = useAuth();
  const sub = user?.subscription;
  const isActive = sub?.status === "active";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-10" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.4em] font-newspaper text-muted-foreground mb-3">— The Subscription Desk —</div>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tight">Keep the ledger open.</h1>
        <p className="mt-4 text-muted-foreground">
          {sub?.status === "trialing" ? (
            <>You're on a <b>{sub.days_left}</b>-day trial of Premium features (PDF, forecast, ad-free). Volume caps (5 bills / 2 income) apply until you subscribe.</>
          ) : isActive ? (
            <>You're a Premium subscriber. Thank you for supporting The Daily Ledger.</>
          ) : (
            <>Free tier keeps a small ledger open. Premium unlocks the newsroom.</>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free */}
        <Card data-testid="plan-free">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Free</div>
            <div className="mt-3 font-display text-4xl font-black">$0<span className="text-base font-normal text-muted-foreground">/mo</span></div>
            <p className="text-sm text-muted-foreground mt-2">The reader's edition.</p>
            <ul className="mt-6 space-y-2 text-sm flex-1">
              <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Up to 5 bills</li>
              <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Up to 2 income streams</li>
              <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Monthly calendar with running net</li>
              <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Per-occurrence paid marking</li>
              <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> CSV export</li>
              <li className="text-xs text-muted-foreground italic pl-6">· Includes tasteful ads</li>
            </ul>
            <Button variant="outline" className="mt-6 w-full" disabled data-testid="plan-free-btn">Current tier baseline</Button>
          </CardContent>
        </Card>

        {/* Monthly */}
        <Card className="border-2 border-[hsl(var(--primary))] relative" data-testid="plan-monthly">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] uppercase tracking-widest px-2 py-1 rounded font-newspaper">Popular</div>
          <CardContent className="p-6 flex flex-col h-full">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--primary))] font-bold flex items-center gap-1"><Crown size={12}/> Premium · Monthly</div>
            <div className="mt-3 font-display text-4xl font-black">$4.99<span className="text-base font-normal text-muted-foreground">/mo</span></div>
            <p className="text-sm text-muted-foreground mt-2">Cancel anytime.</p>
            <ul className="mt-6 space-y-2 text-sm flex-1">
              {FEATURES.map((f) => (
                <li key={f} className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> {f}</li>
              ))}
            </ul>
            {isActive ? (
              <Button className="mt-6 w-full" onClick={openPortal} data-testid="manage-billing-btn">Manage billing</Button>
            ) : (
              <Button className="mt-6 w-full" onClick={()=>startCheckout("premium_monthly")} data-testid="subscribe-monthly-btn">
                <Sparkles size={14} className="mr-1"/> Subscribe monthly
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Yearly */}
        <Card data-testid="plan-yearly">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1"><Crown size={12}/> Premium · Yearly</div>
            <div className="mt-3 font-display text-4xl font-black">$39<span className="text-base font-normal text-muted-foreground">/yr</span></div>
            <p className="text-sm text-[hsl(var(--paid-text))] mt-2 font-semibold">Save 35% · $3.25/mo</p>
            <ul className="mt-6 space-y-2 text-sm flex-1">
              {FEATURES.map((f) => (
                <li key={f} className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> {f}</li>
              ))}
            </ul>
            {isActive ? (
              <Button variant="outline" className="mt-6 w-full" onClick={openPortal} data-testid="manage-billing-btn-yearly">Manage billing</Button>
            ) : (
              <Button variant="outline" className="mt-6 w-full" onClick={()=>startCheckout("premium_yearly")} data-testid="subscribe-yearly-btn">
                Save with yearly
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="text-center text-xs text-muted-foreground font-newspaper italic">
        Payments securely processed by Stripe. Prices in USD. Applicable taxes handled automatically at checkout.
      </div>
    </div>
  );
}
